import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Plus, Trash2, Save, Pencil, X, Clock, Users2, Video, ExternalLink, ClipboardList, BarChart3, CheckCircle2, CalendarX } from "lucide-react";
import { nexusApi, apiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const DIAS = [["0", "Dom"], ["1", "Lun"], ["2", "Mar"], ["3", "Mié"], ["4", "Jue"], ["5", "Vie"], ["6", "Sáb"]];
const TA = (props) => (<textarea {...props} className={`w-full rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm text-[#0A1C30] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 ${props.className || ""}`} />);

const BLANK = {
  nombre: "", descripcion: "", color: "#C9A227", duracion_min: 30, activo: true, orden: 0,
  host_mode: "staff", host_id: "", host_email: "", host_nombre: "",
  dias_semana: [1, 3, 5], horarios: [{ inicio: "16:00", fin: "18:00" }],
  fecha_inicio: "", fecha_fin: "", target_tipo: "todos", target_grupo: "", target_user_id: "",
};

export default function NexusAdmin() {
  const [tab, setTab] = useState("tipos");
  const [types, setTypes] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [dias, setDias] = useState(0);
  const [edit, setEdit] = useState(null); // objeto tipo en edición (o BLANK)

  const loadTypes = () => nexusApi.adminTypes().then(setTypes).catch(() => {});
  useEffect(() => {
    loadTypes();
    nexusApi.hosts().then(setHosts).catch(() => {});
  }, []);
  const loadBookings = () => nexusApi.adminBookings().then((d) => { setBookings(d.bookings || []); setStatusOptions(d.status_options || []); }).catch(() => {});
  useEffect(() => {
    if (tab === "reservas") loadBookings();
    if (tab === "config") nexusApi.adminConfig().then(setCfg).catch(() => {});
    if (tab === "metricas") nexusApi.metrics(dias).then(setMetrics).catch(() => {});
  }, [tab, dias]);

  const setResultado = async (b, resultado) => {
    try { await nexusApi.updateBooking(b.id, { resultado }); toast.success("Estatus actualizado"); loadBookings(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const startNew = () => setEdit({ ...BLANK });
  const startEdit = (t) => setEdit({ ...BLANK, ...t, dias_semana: (t.dias_semana || []).map(Number) });

  const toggleDia = (n) => {
    const has = edit.dias_semana.includes(n);
    setEdit({ ...edit, dias_semana: has ? edit.dias_semana.filter((x) => x !== n) : [...edit.dias_semana, n] });
  };
  const setHorario = (i, field, val) => {
    const h = [...edit.horarios]; h[i] = { ...h[i], [field]: val }; setEdit({ ...edit, horarios: h });
  };
  const addHorario = () => setEdit({ ...edit, horarios: [...edit.horarios, { inicio: "16:00", fin: "18:00" }] });
  const delHorario = (i) => setEdit({ ...edit, horarios: edit.horarios.filter((_, x) => x !== i) });

  const save = async () => {
    if (!edit.nombre.trim()) return toast.error("Escribe un nombre para la reunión.");
    const payload = { ...edit, duracion_min: Number(edit.duracion_min) || 30 };
    if (edit.host_mode === "instructor" && edit.host_id) {
      const h = hosts.find((x) => x.id === edit.host_id);
      if (h) { payload.host_email = h.email; payload.host_nombre = h.nombre; }
    } else if (edit.host_mode === "staff") {
      payload.host_id = ""; payload.host_email = ""; payload.host_nombre = "";
    }
    try {
      if (edit.id) await nexusApi.updateType(edit.id, payload);
      else await nexusApi.createType(payload);
      toast.success("Reunión guardada");
      setEdit(null); loadTypes();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (t) => {
    if (!window.confirm(`¿Eliminar la reunión "${t.nombre}"?`)) return;
    try { await nexusApi.deleteType(t.id); toast.success("Eliminada"); loadTypes(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveCfg = async () => {
    try { await nexusApi.saveConfig(cfg); toast.success("Configuración guardada"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="nexus-admin" className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#1E6F5C] text-white flex items-center justify-center"><CalendarClock className="w-5 h-5" /></div>
        <div>
          <h1 className="text-2xl font-extrabold text-[#0A1C30]">Supera Nexus</h1>
          <p className="text-sm text-[#6B7688]">Agenda de reuniones · define tipos, disponibilidad y revisa reservas.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-black/5">
        {[["tipos", "Tipos de reunión"], ["reservas", "Reservas"], ["metricas", "Métricas"], ["config", "Configuración"]].map(([id, label]) => (
          <button key={id} data-testid={`nexus-admin-tab-${id}`} onClick={() => { setTab(id); setEdit(null); }}
            className={`px-4 py-2.5 text-sm font-bold rounded-t-xl transition-colors ${tab === id ? "bg-[#0A1C30] text-white" : "text-[#6B7688] hover:bg-black/5"}`}>{label}</button>
        ))}
      </div>

      {/* TIPOS */}
      {tab === "tipos" && !edit && (
        <div className="space-y-3">
          <button data-testid="nexus-new-type" onClick={startNew} className="flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-xl bg-[var(--gold)] text-[#0A1C30]"><Plus className="w-4 h-4" /> Nueva reunión</button>
          {types.map((t) => (
            <div key={t.id} className="rounded-2xl border border-black/10 bg-white p-4 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: (t.color || "#C9A227") + "22" }}><CalendarClock className="w-5 h-5" style={{ color: t.color }} /></span>
                <div>
                  <div className="text-sm font-extrabold text-[#0A1C30] flex items-center gap-2">{t.nombre}{!t.activo && <span className="text-[10px] bg-black/10 text-[#6B7688] px-2 py-0.5 rounded-full">Inactiva</span>}</div>
                  <div className="text-xs text-[#6B7688] mt-0.5">{t.duracion_min} min · {(t.dias_semana || []).map((d) => DIAS[d]?.[1]).join(", ") || "sin días"} · {(t.horarios || []).map((h) => `${h.inicio}-${h.fin}`).join(", ")}</div>
                  <div className="text-[11px] text-[#8a94a6] mt-0.5">Anfitrión: {t.host_mode === "instructor" ? (t.host_nombre || "Instructor") : "Staff / Super Admin"} · Ofrecida a: {t.target_tipo === "todos" ? "Todos" : t.target_tipo === "grupo" ? `Grupo ${t.target_grupo}` : "Usuario específico"}</div>
                </div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`nexus-edit-type-${t.id}`} onClick={() => startEdit(t)} className="p-2 rounded-lg text-[#6B7688] hover:bg-black/5"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(t)} className="p-2 rounded-lg text-[#6B7688] hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* EDITOR */}
      {tab === "tipos" && edit && (
        <div data-testid="nexus-type-editor" className="rounded-2xl border border-black/10 bg-white p-5 space-y-4 max-w-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-extrabold text-[#0A1C30]">{edit.id ? "Editar reunión" : "Nueva reunión"}</h2>
            <button onClick={() => setEdit(null)} className="p-1.5 rounded-lg text-[#6B7688] hover:bg-black/5"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Nombre</Label><Input data-testid="nexus-f-nombre" value={edit.nombre} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} /></div>
            <div><Label className="text-xs">Duración (min)</Label><Input data-testid="nexus-f-duracion" type="number" value={edit.duracion_min} onChange={(e) => setEdit({ ...edit, duracion_min: e.target.value })} /></div>
          </div>
          <div><Label className="text-xs">Descripción</Label><TA rows={2} value={edit.descripcion} onChange={(e) => setEdit({ ...edit, descripcion: e.target.value })} /></div>
          <div className="flex items-center gap-4">
            <div><Label className="text-xs">Color</Label><input type="color" value={edit.color} onChange={(e) => setEdit({ ...edit, color: e.target.value })} className="block w-16 h-9 rounded-lg border border-black/10" /></div>
            <label className="flex items-center gap-2 text-sm font-bold text-[#0A1C30] mt-4"><Switch checked={edit.activo} onCheckedChange={(v) => setEdit({ ...edit, activo: v })} /> Activa</label>
          </div>

          {/* Disponibilidad */}
          <div className="rounded-xl bg-[#F4F6F9] p-4 space-y-3">
            <div className="text-sm font-extrabold text-[#0A1C30] flex items-center gap-2"><Clock className="w-4 h-4" /> Disponibilidad</div>
            <div>
              <Label className="text-xs">Días de la semana</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {DIAS.map(([n, l]) => (
                  <button key={n} data-testid={`nexus-dia-${n}`} onClick={() => toggleDia(Number(n))} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${edit.dias_semana.includes(Number(n)) ? "bg-[#1E6F5C] text-white" : "bg-white text-[#6B7688] border border-black/10"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Rangos de hora (hora de México)</Label>
              {edit.horarios.map((h, i) => (
                <div key={i} className="flex items-center gap-2 mt-1.5">
                  <Input type="time" value={h.inicio} onChange={(e) => setHorario(i, "inicio", e.target.value)} className="w-32" />
                  <span className="text-[#6B7688]">a</span>
                  <Input type="time" value={h.fin} onChange={(e) => setHorario(i, "fin", e.target.value)} className="w-32" />
                  <button onClick={() => delHorario(i)} className="p-1.5 rounded-lg text-[#6B7688] hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button onClick={addHorario} className="mt-2 text-xs font-bold text-[#1E6F5C] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Agregar rango</button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Desde (fecha)</Label><Input type="date" value={edit.fecha_inicio} onChange={(e) => setEdit({ ...edit, fecha_inicio: e.target.value })} /></div>
              <div><Label className="text-xs">Hasta (fecha)</Label><Input type="date" value={edit.fecha_fin} onChange={(e) => setEdit({ ...edit, fecha_fin: e.target.value })} /></div>
            </div>
            <p className="text-[11px] text-[#8a94a6]">Los usuarios verán estos horarios convertidos a su zona local automáticamente.</p>
          </div>

          {/* Anfitrión */}
          <div className="rounded-xl bg-[#F4F6F9] p-4 space-y-3">
            <div className="text-sm font-extrabold text-[#0A1C30] flex items-center gap-2"><Video className="w-4 h-4" /> Anfitrión de la videollamada</div>
            <div className="flex gap-2">
              {[["staff", "Staff / Super Admin"], ["instructor", "Instructor específico"]].map(([v, l]) => (
                <button key={v} data-testid={`nexus-host-${v}`} onClick={() => setEdit({ ...edit, host_mode: v })} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${edit.host_mode === v ? "bg-[#0A1C30] text-white" : "bg-white text-[#6B7688] border border-black/10"}`}>{l}</button>
              ))}
            </div>
            {edit.host_mode === "instructor" && (
              <select data-testid="nexus-host-select" value={edit.host_id} onChange={(e) => setEdit({ ...edit, host_id: e.target.value })} className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm">
                <option value="">Selecciona un instructor…</option>
                {hosts.map((h) => <option key={h.id} value={h.id}>{h.nombre} ({h.email})</option>)}
              </select>
            )}
          </div>

          {/* Disponible para */}
          <div className="rounded-xl bg-[#F4F6F9] p-4 space-y-3">
            <div className="text-sm font-extrabold text-[#0A1C30] flex items-center gap-2"><Users2 className="w-4 h-4" /> ¿A quién se ofrece?</div>
            <div className="flex flex-wrap gap-2">
              {[["todos", "Todos los grupos"], ["grupo", "Un grupo específico"], ["usuario", "Un usuario único"]].map(([v, l]) => (
                <button key={v} data-testid={`nexus-target-${v}`} onClick={() => setEdit({ ...edit, target_tipo: v })} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${edit.target_tipo === v ? "bg-[#1E6F5C] text-white" : "bg-white text-[#6B7688] border border-black/10"}`}>{l}</button>
              ))}
            </div>
            {edit.target_tipo === "grupo" && <div><Label className="text-xs">Nombre del grupo (ej. "Grupo Martes 7pm")</Label><Input data-testid="nexus-target-grupo" value={edit.target_grupo} onChange={(e) => setEdit({ ...edit, target_grupo: e.target.value })} /></div>}
            {edit.target_tipo === "usuario" && <div><Label className="text-xs">ID del usuario</Label><Input data-testid="nexus-target-user" value={edit.target_user_id} onChange={(e) => setEdit({ ...edit, target_user_id: e.target.value })} /></div>}
          </div>

          <button data-testid="nexus-save-type" onClick={save} className="w-full flex items-center justify-center gap-2 text-sm font-extrabold px-4 py-3 rounded-xl bg-[var(--gold)] text-[#0A1C30]"><Save className="w-4 h-4" /> Guardar reunión</button>
        </div>
      )}

      {/* RESERVAS */}
      {tab === "reservas" && (
        <div className="space-y-2" data-testid="nexus-admin-bookings">
          {bookings.length === 0 && <p className="text-sm text-[#6B7688]">Aún no hay reservas.</p>}
          {bookings.map((b) => (
            <div key={b.id} className="rounded-2xl border border-black/10 bg-white p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-extrabold text-[#0A1C30]">{b.meeting_type_nombre} · {b.candidato}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${b.status === "confirmada" ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-500"}`}>{b.status}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5 mt-2 text-xs text-[#6B7688]">
                <div>🕒 <b>Usuario:</b> {b.hora_usuario} ({b.tz_user})</div>
                <div>🇲🇽 <b>México:</b> {b.hora_mexico}</div>
                <div>País: {b.pais} · Edad: {b.edad}</div>
                <div>WhatsApp: {b.whatsapp}</div>
                <div>Padre/Madre: {b.padre}</div>
                <div>Referencia: {b.referencia}</div>
              </div>
              {b.room_url && b.status === "confirmada" && (
                <a href={b.room_url} className="inline-flex items-center gap-1.5 mt-2 text-xs font-bold text-[#1E6F5C]"><Video className="w-4 h-4" /> Entrar a la sala <ExternalLink className="w-3 h-3" /></a>
              )}
              {/* Estatus post-reunión */}
              <div className="mt-3 pt-3 border-t border-black/5">
                <div className="flex items-center flex-wrap gap-2">
                  <span className="text-xs font-bold text-[#6B7688]">{b.is_past ? "Resultado de la reunión:" : "Resultado (disponible tras la hora de la reunión):"}</span>
                  {b.resultado_label ? (
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: (b.resultado_color || "#6B7688") + "22", color: b.resultado_color || "#6B7688" }}>{b.resultado_label}</span>
                  ) : <span className="text-[11px] text-[#8a94a6]">Sin registrar</span>}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {statusOptions.map((o) => (
                    <button key={o.key} data-testid={`nexus-set-status-${b.id}-${o.key}`} onClick={() => setResultado(b, o.key)}
                      className={`text-[11px] font-bold px-2.5 py-1 rounded-lg border transition-colors ${b.resultado === o.key ? "text-white" : "text-[#0A1C30] bg-white hover:bg-black/5"}`}
                      style={b.resultado === o.key ? { background: o.color || "#0A1C30", borderColor: o.color || "#0A1C30" } : { borderColor: "rgba(0,0,0,0.1)" }}>
                      {o.label}
                    </button>
                  ))}
                  {b.resultado && (
                    <button onClick={() => setResultado(b, "")} className="text-[11px] font-bold px-2.5 py-1 rounded-lg text-[#6B7688] bg-black/5">Limpiar</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MÉTRICAS */}
      {tab === "metricas" && (
        <div data-testid="nexus-metrics" className="space-y-5">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-[#6B7688]">Periodo:</span>
            {[[0, "Todo"], [30, "30 días"], [90, "90 días"]].map(([v, l]) => (
              <button key={v} data-testid={`nexus-metrics-periodo-${v}`} onClick={() => setDias(v)} className={`text-xs font-bold px-3 py-1.5 rounded-lg ${dias === v ? "bg-[#0A1C30] text-white" : "bg-black/5 text-[#6B7688]"}`}>{l}</button>
            ))}
          </div>
          {!metrics ? <p className="text-sm text-[#6B7688]">Cargando métricas…</p> : (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ["Total agendadas", metrics.total, "#0A1C30", CalendarClock],
                  ["Próximas", metrics.proximas, "#C9A227", Clock],
                  ["Realizadas", metrics.asistencia.realizadas, "#1E6F5C", CheckCircle2],
                  ["Ausencias", metrics.asistencia.ausencias, "#b45309", CalendarX],
                ].map(([label, val, color, Icon]) => (
                  <div key={label} className="rounded-2xl bg-white ring-1 ring-black/5 p-4">
                    <div className="flex items-center gap-2 text-xs font-bold text-[#6B7688]"><Icon className="w-4 h-4" style={{ color }} /> {label}</div>
                    <div className="text-3xl font-extrabold mt-1" style={{ color }}>{val}</div>
                  </div>
                ))}
              </div>

              {/* Tasa de asistencia */}
              <div className="rounded-2xl bg-white ring-1 ring-black/5 p-5">
                <div className="text-sm font-extrabold text-[#0A1C30] flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#1E6F5C]" /> Tasa de asistencia</div>
                <div className="flex items-center gap-4 mt-3">
                  <div className="text-4xl font-extrabold text-[#1E6F5C]">{metrics.asistencia.tasa}%</div>
                  <div className="flex-1">
                    <div className="h-3 rounded-full bg-black/10 overflow-hidden">
                      <div className="h-full bg-[#1E6F5C]" style={{ width: `${metrics.asistencia.tasa}%` }} />
                    </div>
                    <div className="text-xs text-[#6B7688] mt-1">{metrics.asistencia.realizadas} realizadas de {metrics.asistencia.evaluadas} reuniones evaluadas (realizadas + ausencias). {metrics.sin_resultado_pasadas > 0 && `${metrics.sin_resultado_pasadas} pasadas sin registrar.`}</div>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Por tipo */}
                <div className="rounded-2xl bg-white ring-1 ring-black/5 p-5">
                  <div className="text-sm font-extrabold text-[#0A1C30] mb-3">Reuniones por tipo</div>
                  {metrics.por_tipo.length === 0 ? <p className="text-sm text-[#6B7688]">Sin datos.</p> : (
                    <ResponsiveContainer width="100%" height={Math.max(120, metrics.por_tipo.length * 46)}>
                      <BarChart data={metrics.por_tipo} layout="vertical" margin={{ left: 10, right: 20 }}>
                        <XAxis type="number" allowDecimals={false} hide />
                        <YAxis type="category" dataKey="nombre" width={130} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" radius={[0, 6, 6, 0]} fill="#0A1C30" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {/* Por resultado */}
                <div className="rounded-2xl bg-white ring-1 ring-black/5 p-5">
                  <div className="text-sm font-extrabold text-[#0A1C30] mb-3">Por resultado</div>
                  <div className="space-y-2">
                    {metrics.por_resultado.map((r) => (
                      <div key={r.key} className="flex items-center gap-3">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }} />
                        <span className="text-sm text-[#0A1C30] flex-1">{r.label}</span>
                        <span className="text-sm font-extrabold text-[#0A1C30]">{r.count}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-3 pt-1 border-t border-black/5">
                      <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                      <span className="text-sm text-[#6B7688] flex-1">Canceladas</span>
                      <span className="text-sm font-extrabold text-[#6B7688]">{metrics.canceladas}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Por anfitrión */}
              <div className="rounded-2xl bg-white ring-1 ring-black/5 p-5">
                <div className="text-sm font-extrabold text-[#0A1C30] mb-3 flex items-center gap-2"><Users2 className="w-4 h-4" /> Por asesor / instructor</div>
                {metrics.por_host.length === 0 ? <p className="text-sm text-[#6B7688]">Sin datos.</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-xs text-[#6B7688] border-b border-black/5">
                        <th className="py-2">Anfitrión</th><th className="py-2 text-center">Total</th><th className="py-2 text-center">Realizadas</th><th className="py-2 text-center">Ausencias</th><th className="py-2 text-center">Asistencia</th>
                      </tr></thead>
                      <tbody>
                        {metrics.por_host.map((h) => (
                          <tr key={h.host} className="border-b border-black/5">
                            <td className="py-2 font-bold text-[#0A1C30]">{h.host}</td>
                            <td className="py-2 text-center">{h.total}</td>
                            <td className="py-2 text-center text-[#1E6F5C] font-bold">{h.realizadas}</td>
                            <td className="py-2 text-center text-[#b45309] font-bold">{h.ausencias}</td>
                            <td className="py-2 text-center"><span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#1E6F5C]/10 text-[#1E6F5C]">{h.tasa}%</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* CONFIG */}
      {tab === "config" && cfg && (
        <div className="rounded-2xl border border-black/10 bg-white p-5 space-y-4 max-w-2xl" data-testid="nexus-admin-config">
          <div><Label className="text-xs">Título (visible al usuario)</Label><Input value={cfg.titulo || ""} onChange={(e) => setCfg({ ...cfg, titulo: e.target.value })} /></div>
          <div><Label className="text-xs">Introducción</Label><TA rows={2} value={cfg.intro || ""} onChange={(e) => setCfg({ ...cfg, intro: e.target.value })} /></div>
          <div>
            <Label className="text-xs">Instrucciones (una por línea)</Label>
            <TA rows={4} value={(cfg.instrucciones || []).join("\n")} onChange={(e) => setCfg({ ...cfg, instrucciones: e.target.value.split("\n").filter((x) => x.trim()) })} />
          </div>
          <div><Label className="text-xs">WhatsApp del asesor (para confirmación asistida, ej. 525578883198)</Label><Input value={cfg.wa_asesor_number || ""} onChange={(e) => setCfg({ ...cfg, wa_asesor_number: e.target.value })} /></div>

          {/* Recordatorios automáticos */}
          <div className="rounded-xl bg-[#F4F6F9] p-4 space-y-2">
            <div className="text-sm font-extrabold text-[#0A1C30] flex items-center gap-2"><Clock className="w-4 h-4" /> Recordatorios automáticos</div>
            <p className="text-[11px] text-[#8a94a6]">Se envían antes de cada reunión confirmada, a la hora del usuario.</p>
            {(cfg.reminders || []).map((r, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 bg-white rounded-lg p-2 border border-black/5">
                <Input type="number" value={r.offset_minutes} onChange={(e) => { const a = [...cfg.reminders]; a[i] = { ...a[i], offset_minutes: Number(e.target.value) }; setCfg({ ...cfg, reminders: a }); }} className="w-24" />
                <span className="text-xs text-[#6B7688]">min antes ·</span>
                <select value={r.channel || "both"} onChange={(e) => { const a = [...cfg.reminders]; a[i] = { ...a[i], channel: e.target.value }; setCfg({ ...cfg, reminders: a }); }} className="rounded-lg border border-black/10 px-2 py-1.5 text-sm">
                  <option value="both">Correo + notificación</option>
                  <option value="email">Solo correo</option>
                  <option value="push">Solo notificación</option>
                </select>
                <Input placeholder="Etiqueta (ej. 1 hora antes)" value={r.label || ""} onChange={(e) => { const a = [...cfg.reminders]; a[i] = { ...a[i], label: e.target.value }; setCfg({ ...cfg, reminders: a }); }} className="w-40" />
                <button onClick={() => setCfg({ ...cfg, reminders: cfg.reminders.filter((_, x) => x !== i) })} className="p-1.5 rounded-lg text-[#6B7688] hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button data-testid="nexus-add-reminder" onClick={() => setCfg({ ...cfg, reminders: [...(cfg.reminders || []), { offset_minutes: 60, channel: "both", label: "" }] })} className="text-xs font-bold text-[#1E6F5C] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Agregar recordatorio</button>
          </div>

          {/* Opciones de estatus post-reunión */}
          <div className="rounded-xl bg-[#F4F6F9] p-4 space-y-2">
            <div className="text-sm font-extrabold text-[#0A1C30] flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Estatus de resultado de reunión</div>
            <p className="text-[11px] text-[#8a94a6]">Opciones que el admin podrá asignar a cada reunión (realizada, cancelada, ausencia, etc.).</p>
            {(cfg.status_options || []).map((o, i) => (
              <div key={i} className="flex items-center gap-2 bg-white rounded-lg p-2 border border-black/5">
                <input type="color" value={o.color || "#1E6F5C"} onChange={(e) => { const a = [...cfg.status_options]; a[i] = { ...a[i], color: e.target.value }; setCfg({ ...cfg, status_options: a }); }} className="w-9 h-8 rounded border border-black/10" />
                <Input value={o.label} onChange={(e) => { const a = [...cfg.status_options]; a[i] = { ...a[i], label: e.target.value, key: (a[i].key || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_")) }; setCfg({ ...cfg, status_options: a }); }} className="flex-1" />
                <button onClick={() => setCfg({ ...cfg, status_options: cfg.status_options.filter((_, x) => x !== i) })} className="p-1.5 rounded-lg text-[#6B7688] hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button data-testid="nexus-add-status" onClick={() => setCfg({ ...cfg, status_options: [...(cfg.status_options || []), { key: `status_${Date.now()}`, label: "Nuevo estatus", color: "#0A1C30" }] })} className="text-xs font-bold text-[#1E6F5C] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Agregar estatus</button>
          </div>

          <button data-testid="nexus-save-config" onClick={saveCfg} className="flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-xl bg-[var(--gold)] text-[#0A1C30]"><Save className="w-4 h-4" /> Guardar</button>
        </div>
      )}
    </div>
  );
}
